import React, { useEffect, useState, useRef } from "react";
import { Container, Row, Col, Button, Card } from "react-bootstrap";
import { motion, useScroll, useTransform, useSpring, useInView } from "framer-motion";
import {
  FaFilm,
  FaTicketAlt,
  FaShieldAlt,
  FaMobile,
  FaMapMarkerAlt,
  FaStar,
  FaUsers,
  FaClock,
  FaHeart,
  FaPlay,
  FaAward,
  FaGlobe,
} from "react-icons/fa";
import "./About.css";
import { Link } from "react-router-dom";

const About = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  // Hero Parallax
  const heroY = useTransform(smoothProgress, [0, 0.2], [0, 150]);
  const heroOpacity = useTransform(smoothProgress, [0, 0.15], [1, 0]);
  const heroScale = useTransform(smoothProgress, [0, 0.2], [1, 1.1]);

  const [counters, setCounters] = useState({
    movies: 0,
    users: 0,
    bookings: 0,
  });

  const statsRef = useRef(null);
  const statsInView = useInView(statsRef, { once: true, amount: 0.5 });

  useEffect(() => {
    if (statsInView) {
      const targets = { movies: 500, users: 10000, bookings: 50000 };
      const duration = 2000;
      const steps = 60;
      const stepTime = duration / steps;

      let step = 0;
      const timer = setInterval(() => {
        step++;
        const progress = step / steps;
        setCounters({
          movies: Math.floor(targets.movies * progress),
          users: Math.floor(targets.users * progress),
          bookings: Math.floor(targets.bookings * progress),
        });

        if (step >= steps) {
          clearInterval(timer);
          setCounters(targets);
        }
      }, stepTime);
      return () => clearInterval(timer);
    }
  }, [statsInView]);

  const features = [
    {
      icon: <FaFilm />,
      title: "Global Premieres",
      description: "Exclusive access to international blockbusters and screenings.",
    },
    {
      icon: <FaTicketAlt />,
      title: "Seamless Ticketing",
      description: "Fast, secure seat reservations in under 60 seconds.",
    },
    {
      icon: <FaShieldAlt />,
      title: "Secure Ecosystem",
      description: "Bank-level encryption for every transaction and account.",
    },
    {
      icon: <FaMobile />,
      title: "Omnichannel Design",
      description: "High-fidelity experience across all your digital devices.",
    },
    {
      icon: <FaMapMarkerAlt />,
      title: "Nationwide Network",
      description: "Integrated with 500+ premium multiplexes nationwide.",
    },
    {
      icon: <FaStar />,
      title: "VIP Concierge",
      description: "Unlock loyalty rewards and curated movie recommendations.",
    },
  ];

  return (
    <div className="about-page-v2" ref={containerRef}>
      {/* Cinematic Hero */}
      <section className="about-hero-section">
        <motion.div
          className="hero-media-wrapper"
          style={{ y: heroY, scale: heroScale }}
        >
          <img src="/assets/about/hero.png" alt="Cinema Hero" className="hero-img" />
          <div className="hero-overlay-gradient"></div>
        </motion.div>

        <Container className="hero-content-container">
          <motion.div
            className="hero-text-content"
            style={{ opacity: heroOpacity }}
          >
            <motion.span
              className="hero-subtitle"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              CRAFTING THE FUTURE OF ENTERTAINMENT
            </motion.span>
            <motion.h1
              className="hero-title"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              The Ultimate Gateway to <br />
              <span>Cinematic Magic</span>
            </motion.h1>
            <motion.div
              className="hero-cta"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 1 }}
            >
              <Link to="/movies" className="btn-cinematic">
                EXPLORE NOW
              </Link>
            </motion.div>
          </motion.div>
        </Container>

        <div className="scroll-indicator">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="scroll-dot"
          />
        </div>
      </section>

      {/* Intro Section - The Vision */}
      <section className="vision-section">
        <Container>
          <Row className="align-items-center">
            <Col lg={6}>
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="vision-copy"
              >
                <div className="section-label">OUR PERSPECTIVE</div>
                <h2 className="section-title">Where Innovation <br />Meets Emotion</h2>
                <p className="section-text">
                  Established in 2024, we dissolve the barrier between the
                  spectator and the screen. Using cloud-native technology,
                  we ensure every interaction is as seamless as a classic film edit.
                </p>
                <div className="vision-highlights">
                  <div className="highlight-item">
                    <FaAward className="hl-icon" />
                    <span>Industry-Leading Booking Infrastructure</span>
                  </div>
                  <div className="highlight-item">
                    <FaGlobe className="hl-icon" />
                    <span>Nationwide Distributed Network</span>
                  </div>
                </div>
              </motion.div>
            </Col>
            <Col lg={6}>
              <motion.div
                className="vision-image-stack"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1 }}
                viewport={{ once: true }}
              >
                <img src="/assets/about/projector.png" alt="Vision" className="vision-main-img" />
                <div className="img-accent-square"></div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Stats - Cinematic Counter */}
      <section className="stats-section" ref={statsRef}>
        <Container>
          <Row className="text-center g-5">
            <Col md={4}>
              <div className="stat-card">
                <div className="stat-number">{counters.movies.toLocaleString()}+</div>
                <div className="stat-label">Blockbusters Listing</div>
              </div>
            </Col>
            <Col md={4}>
              <div className="stat-card">
                <div className="stat-number">{(counters.users / 1000).toFixed(0)}k+</div>
                <div className="stat-label">Passionate Users</div>
              </div>
            </Col>
            <Col md={4}>
              <div className="stat-card">
                <div className="stat-number">{(counters.bookings / 1000).toFixed(0)}k+</div>
                <div className="stat-label">Tickets Secured</div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Features - Modern Grid */}
      <section className="features-section">
        <Container>
          <div className="text-center mb-5">
            <div className="section-label">WHY CHOOSE US</div>
            <h2 className="section-title">Excellence in Every Frame</h2>
          </div>
          <Row className="g-4">
            {features.map((f, i) => (
              <Col key={i} lg={4} md={6}>
                <motion.div
                  className="feature-modern-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -10 }}
                >
                  <div className="feature-icon-wrapper">{f.icon}</div>
                  <h3>{f.title}</h3>
                  <p>{f.description}</p>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Parallax Quote Section */}
      <section className="parallax-quote-section">
        <div className="parallax-bg" style={{ backgroundImage: "url('/assets/about/comm.png')" }}></div>
        <div className="parallax-overlay"></div>
        <Container className="position-relative">
          <motion.div
            className="quote-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
          >
            <div className="quote-mark">“</div>
            <blockquote className="cinematic-quote">
              "Where the art of storytelling meets the precision of <span>technology</span>. We deliver a seamless gateway to immersive cinema."
            </blockquote>
            <p className="quote-author">— The Book My Cinema Team</p>
          </motion.div>
        </Container>
      </section>

      {/* Mission / Values Section */}
      <section className="values-section">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="order-2 order-lg-1">
              <div className="values-grid">
                <div className="value-box">
                  <h4>Innovation</h4>
                  <p>Constantly pushing the boundaries of movie-tech.</p>
                </div>
                <div className="value-box">
                  <h4>Passion</h4>
                  <p>Fueled by the love of storytelling and community.</p>
                </div>
                <div className="value-box">
                  <h4>Security</h4>
                  <p>Your trust is our most valuable asset.</p>
                </div>
                <div className="value-box">
                  <h4>Community</h4>
                  <p>Bringing people together through shared joy.</p>
                </div>
              </div>
            </Col>
            <Col lg={6} className="order-1 order-lg-2 mb-5 mb-lg-0">
              <div className="mission-copy">
                <div className="section-label">OUR CORE MISSION</div>
                <h2 className="section-title">Revolutionizing the <br />Theatrical Paradigm</h2>
                <p>
                  Unifying the cinematic ecosystem. We combine state-of-the-art
                  reservation systems with human-centric design to elevate your
                  theatrical experience.
                </p>
                <Link to="/movies" className="btn-outline-cinematic">
                  JOIN OUR COMMUNITY
                </Link>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Final CTA */}
      <section className="about-footer-cta">
        <Container className="text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="footer-cta-card"
          >
            <h2>Experience the Premiere.</h2>
            <p>Join the thousands who have already redefined their movie-going journey.</p>
            <Link to="/movies" className="btn-cinematic-large">
              RESERVE NOW
            </Link>
          </motion.div>
        </Container>
      </section>
    </div>
  );
};

export default About;
